import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import ReportsService from '../../../../../../../../../services/reports-service';

const CreateReportModalContent = forwardRef(
  ({ onClose, onSubmit, formData, setFormData }, ref) => {
    const reportService = useMemo(() => new ReportsService(), []);
    const safeFormData = formData || { 
      reportTitle: '', 
      issuer: '', 
      isin: '', 
      bpId: '', 
      file: null, 
      holder: '', 
      dateFrom: '', 
      dateTo: '',
      address: '',
      paymentDate: '',
      maturityDate: '',
      amount: '',
    };
    const safeSetFormData = typeof setFormData === 'function' ? setFormData : () => {};

    // Errors state for validations
    const [errors, setErrors] = useState({});

    const [submitting, setSubmitting] = useState(false);

    // Static report names options
    const reportTitleOptions = [
      { value: '', label: 'Select' },
      { value: 'BIR 2306 Request Letter', label: 'BIR 2306 Request Letter' },
      { value: 'Closure of Registry Service / Payment Account', label: 'Closure of Registry Service / Payment Account' },
      { value: 'Consent Solicitation Entitlement Report Excel', label: 'Consent Solicitation Entitlement Report' },
      // { value: 'Credit Letter', label: 'Credit Letter' },
    ];

    // Local state to store mapping of issuer to ISIN codes (and payment dates) and dropdown options
    const [issuerMapping, setIssuerMapping] = useState({});
    const [issuerOptions, setIssuerOptions] = useState([{ value: '', label: 'Select' }]);
    const [isinOptions, setIsinOptions] = useState([{ value: '', label: 'Select' }]);
    
    // Loading state for API call
    const [loading, setLoading] = useState(true);

    const today = new Date().toISOString().split('T')[0];

    // Fetch issuers and their ISINs/payment dates when the component is rendered
    useEffect(() => {
      reportService
        .eventBasedReportGetIsinIssuer()
        .then((response) => {
          let data = response.body || response;
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data);
            } catch (err) {
              console.error('Error parsing API response:', err);
            }
          }
          if (!data) {
            setLoading(false);
            return;
          }
          setIssuerMapping(data);
          const issuersFromApi = Object.keys(data).map((issuer) => ({
            value: issuer,
            label: issuer,
          }));
          setIssuerOptions([{ value: '', label: 'Select' }, ...issuersFromApi]);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching issuers:', err);
          setLoading(false);
        });
    }, [reportService]);

    // Handler for Issuer change: reset ISIN, BPID, paymentDate
    const handleIssuerChange = (selectedOption) => {
      safeSetFormData({ ...safeFormData, issuer: selectedOption.value, isin: '', bpId: '', paymentDate: '' });
      setErrors((prev) => ({ ...prev, issuer: '', isin: '', bpId: '', paymentDate: '' }));
      if (issuerMapping[selectedOption.value]) {
        const isinsForIssuer = Object.keys(issuerMapping[selectedOption.value]).map((isin) => ({
          value: isin,
          label: isin,
        }));
        setIsinOptions([{ value: '', label: 'Select' }, ...isinsForIssuer]);
      } else {
        setIsinOptions([{ value: '', label: 'Select' }]);
      }
    };

    // Handler for ISIN change: reset bpId, paymentDate
    const handleIsinChange = (selectedOption) => {
      safeSetFormData({ ...safeFormData, isin: selectedOption.value, bpId: '', paymentDate: '' });
      setErrors((prev) => ({ ...prev, isin: '', bpId: '', paymentDate: '' }));
    };

    // Handler for file change: validate extension based on reportTitle
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) {
        safeSetFormData({ ...safeFormData, file: null });
        return;
      }
      const { name } = file;
      const reportTitle = safeFormData.reportTitle;
      let valid = false;
      let allowedDesc = '';
      const ext = name.split('.').pop().toLowerCase();

      if (reportTitle === 'Consent Solicitation Entitlement Report Excel') {
        // Excel: allow .xls or .xlsx
        if (ext === 'xls' || ext === 'xlsx') valid = true;
        allowedDesc = 'Excel file (.xls or .xlsx)';
      } else {
        // non-Excel: allow PDF
        if (ext === 'pdf') valid = true;
        allowedDesc = 'PDF file';
      }

      if (!valid) {
        safeSetFormData({ ...safeFormData, file: null });
        setErrors((prev) => ({
          ...prev,
          file: `Invalid file type. Please upload a ${allowedDesc}.`
        }));
      } else {
        safeSetFormData({ ...safeFormData, file });
        setErrors((prev) => ({ ...prev, file: '' }));
      }
    };

    // Handler for amount formatting
    const handleAmountChange = (e) => {
      let val = e.target.value;
      // remove all commas
      val = val.replace(/,/g, '');
      // allow only digits and optional decimal point
      if (!/^\d*\.?\d*$/.test(val)) {
        return; 
      }
      // split integer and decimal parts
      let [intPart, decPart] = val.split('.');
      // truncate decimal to max 2 digits
      if (decPart !== undefined) {
        decPart = decPart.slice(0, 2);
      }
      // add commas to integer part
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      // re-join
      const formatted = decPart !== undefined ? `${intPart}.${decPart}` : intPart;
      safeSetFormData({ ...safeFormData, amount: formatted });
      setErrors((prev) => ({ ...prev, amount: '' }));
    };

    // Validate required fields before submitting
    const validateFields = () => {
      let newErrors = {};
      if (!safeFormData.reportTitle) {
        newErrors.reportTitle = "Report Name is required";
      }
      if (!safeFormData.issuer) {
        newErrors.issuer = "Issuer is required";
      }
      if (!safeFormData.isin) {
        newErrors.isin = "ISIN is required";
      }
      if (safeFormData.reportTitle === 'BIR 2306 Request Letter') {
        if (!safeFormData.bpId) {
          newErrors.bpId = "BPID is required";
        } else if (safeFormData.bpId.length !== 11) {
          newErrors.bpId = "BPID must be exactly 11 alphanumeric characters";
        }
        if (!safeFormData.holder) {
          newErrors.holder = "Holder Name is required";
        } else if (safeFormData.holder.length > 100) {
          newErrors.holder = "Holder Name must be at most 100 characters";
        }
        if (!safeFormData.dateFrom) {
          newErrors.dateFrom = "Date From is required";
        }
        if (!safeFormData.dateTo) {
          newErrors.dateTo = "Date To is required";
        }
        if (safeFormData.dateFrom && safeFormData.dateTo) {
          const fromD = new Date(safeFormData.dateFrom);
          const toD = new Date(safeFormData.dateTo);
          if (fromD > toD) {
            newErrors.dateFrom = "Date From cannot be later than Date To";
            newErrors.dateTo = "Date To cannot be earlier than Date From";
          }
        }
      }
      if (safeFormData.reportTitle === 'Consent Solicitation Entitlement Report Excel') {
        if (!safeFormData.address) {
          newErrors.address = "Address is required";
        }
        if (!safeFormData.paymentDate) {
          newErrors.paymentDate = "Payment Date is required";
        }
        if (!safeFormData.amount) {
          newErrors.amount = "Amount is required";
        }
      }
      // For file: if not Credit Letter, require file
      if (safeFormData.reportTitle !== 'Credit Letter') {
        if (!safeFormData.file) {
          newErrors.file = "Upload Report is required";
        }
      }
      return newErrors;
    };

    // Handler for Submit that validates required fields before calling onSubmit
    const handleSubmit = async () => {
      const newErrors = validateFields();
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setSubmitting(true);
      try {
        // assume onSubmit returns a Promise
        await onSubmit(safeFormData);
      } catch (err) {
        console.error('Error submitting report:', err);
        // optionally show error toast
      } finally {
        setSubmitting(false);
      }
    };

    // Expose reset method to parent
    useImperativeHandle(ref, () => ({
      reset: () => {
        safeSetFormData({ 
          reportTitle: '', 
          issuer: '', 
          isin: '', 
          bpId: '',
          file: null, 
          holder: '', 
          dateFrom: '', 
          dateTo: '',
          address: '',
          paymentDate: '',
          maturityDate: '',
          amount: '',
        });
        setErrors({});
      },
    }));

    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    return (
      <>
        <Form>
          {/* Report Name */}
          <Form.Group controlId="formReportName">
            <Row className="align-items-center">
              <Col sm={4}>
                <Form.Label>
                  Report Name <span className="text-danger">*</span>
                </Form.Label>
              </Col>
              <Col sm={8}>
                <Select
                  onChange={(e) => {
                    safeSetFormData({ ...safeFormData, reportTitle: e.value });
                    setErrors((prev) => ({ ...prev, reportTitle: '' }));
                  }}
                  options={reportTitleOptions}
                  value={reportTitleOptions.find((option) => option.value === safeFormData.reportTitle)}
                />
                {errors.reportTitle && <Form.Text className="text-danger">{errors.reportTitle}</Form.Text>}
              </Col>
            </Row>
          </Form.Group>

          {/* Issuer */}
          <Form.Group controlId="formIssuer">
            <Row className="align-items-center">
              <Col sm={4}>
                <Form.Label>
                  Issuer <span className="text-danger">*</span>
                </Form.Label>
              </Col>
              <Col sm={8}>
                <Select
                  onChange={handleIssuerChange}
                  options={issuerOptions}
                  value={issuerOptions.find((option) => option.value === safeFormData.issuer)}
                />
                {errors.issuer && <Form.Text className="text-danger">{errors.issuer}</Form.Text>}
              </Col>
            </Row>
          </Form.Group>

          {/* ISIN */}
          <Form.Group controlId="formISIN">
            <Row className="align-items-center">
              <Col sm={4}>
                <Form.Label>
                  ISIN <span className="text-danger">*</span>
                </Form.Label>
              </Col>
              <Col sm={8}>
                <Select
                  onChange={handleIsinChange}
                  options={isinOptions}
                  value={isinOptions.find((option) => option.value === safeFormData.isin)}
                />
                {errors.isin && <Form.Text className="text-danger">{errors.isin}</Form.Text>}
              </Col>
            </Row>
          </Form.Group>

          {/* BPID for BIR 2306 */}
          {safeFormData.reportTitle === 'BIR 2306 Request Letter' && (
            <Form.Group controlId="formBPID">
              <Row className="align-items-center">
                <Col sm={4}>
                  <Form.Label>
                    BPID <span className="text-danger">*</span>
                  </Form.Label>
                </Col>
                <Col sm={8}>
                  <Form.Control
                    type="text"
                    value={safeFormData.bpId || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z0-9]*$/.test(value) && value.length <= 11) {
                        safeSetFormData({ ...safeFormData, bpId: value });
                        setErrors((prev) => ({ ...prev, bpId: '' }));
                      }
                    }}
                    placeholder="11 alphanumeric characters"
                    isInvalid={!!errors.bpId}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.bpId}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </Form.Group>
          )}

          {/* Holder Name & Period Date for BIR 2306 */}
          {safeFormData.reportTitle === 'BIR 2306 Request Letter' && (
            <>
              {/* Holder Name */}
              <Form.Group controlId="formHolderName">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Holder Name <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      value={safeFormData.holder || ''}
                      maxLength={100}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 100) {
                          safeSetFormData({ ...safeFormData, holder: val });
                          setErrors((prev) => ({ ...prev, holder: '' }));
                        } else {
                          // Should not really reach due to maxLength, but just in case
                          setErrors((prev) => ({ ...prev, holder: 'Holder Name must be at most 100 characters' }));
                        }
                      }}
                      isInvalid={!!errors.holder}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.holder}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>

              {/* Period Date */}
              <Form.Group controlId="formPeriodDate">
                <Row>
                  <Col xs={12}>
                    <Form.Label>
                      Period Date <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Control
                      type="date"
                      value={safeFormData.dateFrom || ''}
                      max={safeFormData.dateTo || today}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        safeSetFormData({ ...safeFormData, dateFrom: newFrom });
                        setErrors((prev) => {
                          const nxt = { ...prev, dateFrom: '', dateTo: '' };
                          if (safeFormData.dateTo) {
                            const fromD = new Date(newFrom);
                            const toD = new Date(safeFormData.dateTo);
                            if (fromD > toD) {
                              nxt.dateFrom = "Date From cannot be later than Date To";
                              nxt.dateTo = "Date To cannot be earlier than Date From";
                            }
                          }
                          return nxt;
                        });
                      }}
                      isInvalid={!!errors.dateFrom}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dateFrom}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">Date From</Form.Text>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Control
                      type="date"
                      value={safeFormData.dateTo || ''}
                      min={safeFormData.dateFrom || undefined}
                      // max={today}
                      onChange={(e) => {
                        const newTo = e.target.value;
                        safeSetFormData({ ...safeFormData, dateTo: newTo });
                        setErrors((prev) => {
                          const nxt = { ...prev, dateFrom: '', dateTo: '' };
                          if (safeFormData.dateFrom) {
                            const fromD = new Date(safeFormData.dateFrom);
                            const toD = new Date(newTo);
                            if (fromD > toD) {
                              nxt.dateFrom = "Date From cannot be later than Date To";
                              nxt.dateTo = "Date To cannot be earlier than Date From";
                            }
                          }
                          return nxt;
                        });
                      }}
                      isInvalid={!!errors.dateTo}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dateTo}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">Date To</Form.Text>
                  </Col>
                </Row>
              </Form.Group>
            </>
          )}

          {/* Consent Solicitation Entitlement Report Excel */}
          {safeFormData.reportTitle === 'Consent Solicitation Entitlement Report Excel' && (
            <>
              <Form.Group controlId="formAddress">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      value={safeFormData.address || ''}
                      onChange={(e) => {
                        safeSetFormData({ ...safeFormData, address: e.target.value });
                        setErrors((prev) => ({ ...prev, address: '' }));
                      }}
                      isInvalid={!!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group controlId="formPaymentDate">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Payment Date <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="date"
                      value={safeFormData.paymentDate || ''}
                      onChange={(e) => {
                        safeSetFormData({ ...safeFormData, paymentDate: e.target.value });
                        setErrors((prev) => ({ ...prev, paymentDate: '' }));
                      }}
                      isInvalid={!!errors.paymentDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.paymentDate}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group controlId="formAmount">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Amount <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      value={safeFormData.amount || ''}
                      onChange={handleAmountChange}
                      step=".01"
                      isInvalid={!!errors.amount}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.amount}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>
            </>
          )}

          {/* Closure of Registry Service / Payment Account */}
          {safeFormData.reportTitle === 'Closure of Registry Service / Payment Account' && (
            <Form.Group controlId="formMaturityDate">
              <Row className="align-items-center">
                <Col sm={4}>
                  <Form.Label>Maturity Date</Form.Label>
                </Col>
                <Col sm={8}>
                  <Form.Control
                    type="date"
                    value={safeFormData.maturityDate || ''}
                    onChange={(e) => {
                      safeSetFormData({ ...safeFormData, maturityDate: e.target.value });
                    }}
                  />
                </Col>
              </Row>
            </Form.Group>
          )}

          {/* Credit Letter - if enabled */}
          {safeFormData.reportTitle === 'Credit Letter' && (
            <>
              <Form.Group controlId="formAddress">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      value={safeFormData.address || ''}
                      onChange={(e) => {
                        safeSetFormData({ ...safeFormData, address: e.target.value });
                        setErrors((prev) => ({ ...prev, address: '' }));
                      }}
                      isInvalid={!!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>
              <Form.Group controlId="formPaymentDate">
                <Row className="align-items-center">
                  <Col sm={4}>
                    <Form.Label>
                      Payment Date <span className="text-danger">*</span>
                    </Form.Label>
                  </Col>
                  <Col sm={8}>
                    <Form.Control
                      type="date"
                      value={safeFormData.paymentDate || ''}
                      onChange={(e) => {
                        safeSetFormData({ ...safeFormData, paymentDate: e.target.value });
                        setErrors((prev) => ({ ...prev, paymentDate: '' }));
                      }}
                      isInvalid={!!errors.paymentDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.paymentDate}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              </Form.Group>
            </>
          )}

          {/* Upload Report for non-Credit Letter */}
          {safeFormData.reportTitle !== 'Credit Letter' && (
            <Form.Group controlId="formUpload">
              <Row className="align-items-center">
                <Col sm={4}>
                  <Form.Label>
                    Upload Report <span className="text-danger">*</span>
                  </Form.Label>
                </Col>
                <Col sm={8} className="d-flex align-items-center">
                  <Form.Control
                    type="file"
                    style={{ width: '75%' }}
                    accept={
                      safeFormData.reportTitle === 'Consent Solicitation Entitlement Report Excel'
                        ? '.xls,.xlsx'
                        : '.pdf'
                    }
                    onChange={handleFileChange}
                    isInvalid={!!errors.file}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.file}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </Form.Group>
          )}
        </Form>

        <Row className="mt-3">
          <Col className="d-flex justify-content-start">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  Submitting...
                </>
              ) : (
                'Submit Report'
              )}
            </Button>
          </Col>
        </Row>
      </>
    );
  }
);

export default CreateReportModalContent;
